import { createContext, ReactNode, useCallback, useContext, useEffect, useRef, useState } from "react";
import { Location, useLocation, useNavigate, useNavigationType } from "react-router-dom";
import { lazyDialog, useToast } from "../ui";
import { useNextT } from "../i18n";
import { isApiError } from "../data/errors";
import { ContactCan, useContact } from "../contact/api";
import { historyHref, reportHref, withoutLaunch } from "../contacts/links";

// The dialogs are their own chunks (out of the main bundle): fetched the first time one is asked for, together with
// the contact (the bundler keeps one copy of a fetched chunk, so the dialog's own fetch then finds it there).
const loadReport = () => import("./components/VisitReportDialog").then((m) => ({ default: m.VisitReportDialog }));
const loadHistory = () => import("./components/VisitHistoryDialog").then((m) => ({ default: m.VisitHistoryDialog }));
const VisitReportDialog = lazyDialog(loadReport);
const VisitHistoryDialog = lazyDialog(loadHistory);

/** Starts fetching a dialog's code as soon as an id asks for it. A failure is left to the dialog (it asks again). */
const usePrefetch = (id: number | null, load: () => Promise<unknown>) => {
  useEffect(() => {
    if (id !== null) load().catch(() => undefined);
  }, [id, load]);
};

/** The server refuses ids over 18 digits; anything not a number never asks. */
const ID = /^\d{1,18}$/;
const idOf = (raw: string | null): number | null => (raw !== null && ID.test(raw) && Number(raw) > 0 ? Number(raw) : null);

type Launch = { kind: "report"; raw: string; point: string | null } | { kind: "history"; raw: string } | null;
/** What the address asks for. A report wins over a history (one dialog at a time). */
const readLaunch = (search: string): Launch => {
  const p = new URLSearchParams(search);
  const report = p.get("report");
  if (report !== null) return { kind: "report", raw: report, point: p.get("point") };
  const history = p.get("history");
  return history !== null ? { kind: "history", raw: history } : null;
};

/** Nested providers (the frame's and a page's own) leave the work to the outer one. */
const Mounted = createContext(false);

/** Opens the report over the page the user is on (pushes, so Back closes it). */
export const useOpenReport = () => {
  const navigate = useNavigate();
  const loc = useLocation();
  const here = useRef(loc);
  here.current = loc;
  return useCallback((args: { householdId: number; pointId?: number }) => {
    navigate(reportHref(args.householdId, here.current, args.pointId), { state: here.current.state });
  }, [navigate]);
};

/** Opens the contact's visit history over the page the user is on. */
export const useOpenHistory = () => {
  const navigate = useNavigate();
  const loc = useLocation();
  const here = useRef(loc);
  here.current = loc;
  return useCallback((householdId: number) => {
    navigate(historyHref(householdId, here.current), { state: here.current.state });
  }, [navigate]);
};

type Target = { householdId: number; pointId?: number };

/**
 * Reads `?report=<id>[&point=<id>]` and `?history=<id>` (R16) and shows what they ask for. Closing takes the param
 * off the address and keeps the page's location state (the contact dialog's way back to the list). An id that is not
 * a number is taken off at once.
 */
const Launcher = () => {
  const navigate = useNavigate();
  const loc = useLocation();
  const latest = useRef(loc);
  latest.current = loc;
  const launch = readLaunch(loc.search);
  // Opened by a link on this very page (the entry before is the page without the param): closing steps back to it,
  // so the history never holds the page twice (the contact's ✕ still goes back to the list). Otherwise (a link from
  // elsewhere, the address bar) the param is taken off with a replace. Either way Back does not open it again.
  const navType = useNavigationType();
  const prev = useRef<Location | null>(null);
  const pushedAt = useRef<string | null>(null);
  useEffect(() => {
    const before = prev.current;
    prev.current = loc;
    if (!readLaunch(loc.search)) { pushedAt.current = null; return; }
    const fromHere = navType === "PUSH" && before !== null && before.pathname === loc.pathname && readLaunch(before.search) === null
      && withoutLaunch(before.search) === withoutLaunch(loc.search);
    pushedAt.current = fromHere ? loc.key : null;
  }, [loc, navType]);
  const removeParam = useCallback(() => {
    const l = latest.current;
    if (pushedAt.current === l.key) { pushedAt.current = null; navigate(-1); return; }
    navigate({ pathname: l.pathname, search: withoutLaunch(l.search), hash: l.hash }, { replace: true, state: l.state });
  }, [navigate]);

  const reportId = launch?.kind === "report" ? idOf(launch.raw) : null;
  const pointId = launch?.kind === "report" ? idOf(launch.point) ?? undefined : undefined;
  const invalid = launch !== null && (launch.kind === "report" ? reportId === null : idOf(launch.raw) === null);
  const historyId = launch?.kind === "history" ? idOf(launch.raw) : null;
  useEffect(() => {
    if (invalid) removeParam();
  }, [invalid, removeParam]);

  // Nothing of either is mounted before the first one is asked for; then it stays (its dialog animates closed).
  const [hosted, setHosted] = useState({ report: false, history: false });
  if ((reportId !== null && !hosted.report) || (historyId !== null && !hosted.history)) {
    setHosted({ report: hosted.report || reportId !== null, history: hosted.history || historyId !== null });
  }
  return (
    <>
      {hosted.history && <HistoryHost historyId={historyId} onClose={removeParam} />}
      {hosted.report && <ReportHost reportId={reportId} pointId={pointId} onClose={removeParam} />}
    </>
  );
};

/** A contact the address asks about, and whether its dialog may open (the server's rights on the contact). */
const useAllowed = (id: number | null, may: (can: ContactCan) => boolean, refusal: string, onClose: () => void): boolean => {
  const { t } = useNextT();
  const toast = useToast();
  const contact = useContact(id ?? 0, { enabled: id !== null });
  const mine = contact.data !== undefined && contact.data.id === id;
  // 401 / 402 belong to the app (sign in again / billing): its interceptor moves on, nothing is said or opened here.
  const appHandles = isApiError(contact.error) && (contact.error.status === 401 || contact.error.status === 402);
  const refused = id !== null && !appHandles
    && ((mine && !may(contact.data!.can)) || (isApiError(contact.error) && contact.error.kind === "client"));
  const allowed = id !== null && !refused && !appHandles && (mine ? may(contact.data!.can) : contact.isError);
  /** The contact the refusal was said for: once per link, however often the contact answers again. */
  const toldFor = useRef<number | null>(null);
  useEffect(() => {
    if (id === null) { toldFor.current = null; return; }
    if (!refused || toldFor.current === id) return;
    toldFor.current = id;
    onClose();
    toast({ message: t(refusal) });
  }, [id, refused, onClose, toast, t, refusal]);
  return allowed;
};

/**
 * The visit history the address asks for (F7), once the server said this user may see it (`can.see_history`); else
 * the param goes and a plain toast says so. "Report a visit" in it replaces the param: the history closes and the
 * report opens (one dialog at a time; Back returns to the history).
 */
const HistoryHost = ({ historyId, onClose }: { historyId: number | null; onClose(): void }) => {
  const openReport = useOpenReport();
  usePrefetch(historyId, loadHistory);
  const allowed = useAllowed(historyId, (can) => can.see_history, "visits.errors.history_not_allowed", onClose);
  // The last contact stays mounted while its dialog animates closed.
  const [shown, setShown] = useState<number | null>(null);
  if (allowed && historyId !== null && shown !== historyId) setShown(historyId);
  if (shown === null) return null;
  return (
    <VisitHistoryDialog key={shown} householdId={shown} open={allowed && historyId === shown} onClose={onClose}
      onReport={() => openReport({ householdId: shown })} />
  );
};

/**
 * The report the address asks for, once the server said this user may write it (and the account is not view-only). A
 * 4xx on the contact (gone, or not
 * this user's to see) or `can.report` false never opens: the param goes and a plain toast says so. No answer / 5xx
 * opens the dialog, which shows its own "couldn't load" with Try again.
 */
const ReportHost = ({ reportId, pointId, onClose }: { reportId: number | null; pointId?: number; onClose(): void }) => {
  // View-only accounts may still file a report (the server accepts it, B13); the dialog just hides "Anything to update?".
  usePrefetch(reportId, loadReport);
  const allowed = useAllowed(reportId, (can) => can.report, "visits.launcher.unavailable", onClose);

  // The last target stays mounted while its dialog animates closed (and says "Report saved" after).
  const [target, setTarget] = useState<Target | null>(null);
  if (allowed && reportId !== null && (target?.householdId !== reportId || target?.pointId !== pointId)) setTarget({ householdId: reportId, pointId });
  if (!target) return null;
  return (
    <VisitReportDialog key={`${target.householdId}:${target.pointId ?? ""}`} householdId={target.householdId} pointId={target.pointId}
      open={allowed && target.householdId === reportId && target.pointId === pointId} onClose={onClose} />
  );
};

/**
 * One report launcher for every new page (2D F6): mounted in the new frame inside its ToastProvider once Google Maps
 * is ready (R17), and around a new page shown in today's frame. Inside another launcher it adds nothing.
 */
export const ReportLauncherProvider = ({ children }: { children: ReactNode }) => {
  const mounted = useContext(Mounted);
  if (mounted) return <>{children}</>;
  return (
    <Mounted.Provider value>
      {children}
      <Launcher />
    </Mounted.Provider>
  );
};
